// Angular
import { Component, Input } from '@angular/core';
// Afidium
import { ICondition, RuleType } from './ICondition';
import { ILabels } from 'libraries/interfaces/ILabels';
import { Yii } from 'libraries/interfaces/Yii';
import { BookingType, Keyword } from 'libraries/service/resources/booking/interfaces/get-bookings.enum';
import { ApiPricingRate } from 'libraries/service/resources/booking/interfaces/ApiSearchParameters';
import { faTags, IconDefinition } from "@fortawesome/free-solid-svg-icons";

@Component({
    selector: 'conditions-tab',
    template: `
        <table *ngIf="conditions?.lines?.length" mat-table [dataSource]="conditions.lines">
            <!-- Sector Column -->
            <ng-container matColumnDef="sector">
                <th mat-header-cell *matHeaderCellDef></th>
                <td mat-cell *matCellDef="let element; let i = index;" class="first-column">
                    <table-sector-air-summary *ngIf="element.sector.type === 'air'" [sectors]="element.sector.data"></table-sector-air-summary>
                    <table-sector-stay-summary *ngIf="element.sector.type === 'stay'" [sector]="element.sector.data[0]"></table-sector-stay-summary>
                </td>
            </ng-container>

            <!-- Traveller Column -->
            <ng-container matColumnDef="traveller">
                <th mat-header-cell *matHeaderCellDef>
                    {{ labels.travellers }}
                </th>
                <td mat-cell *matCellDef="let element">
                    <sector-traveller *ngFor="let traveller of element.traveller"
                        [travellerData]="traveller"
                        [abbreviateType]="true"></sector-traveller>
                </td>
            </ng-container>

            <!-- Pricing Column -->
            <ng-container matColumnDef="pricing">
                <th mat-header-cell *matHeaderCellDef>
                    {{ labels.pricing }}
                </th>
                <td mat-cell *matCellDef="let element">
                    <div class="left">
                        <date-validity *ngIf="element?.pricing?.validity" [validity]="element.pricing.validity"></date-validity>

                        <div *ngIf="element.pricing.keywords" class="keywords">exchange.component.ts
                            <fa-icon [icon]="iconTags"></fa-icon>
                            <strong *ngFor="let keyword of element.pricing.keywords; let last = last;">
                                {{ keywordLabels[keyword.xCode] }}<ng-container *ngIf="!last">, </ng-container>
                            </strong>
                        </div>
                        <div *ngIf="element.pricing.company">
                            <span>{{ labels.company }} : </span>
                            <strong>{{ element.pricing.company }}</strong>
                        </div>
                        <div *ngIf="element.pricing.fareBasis">
                            <span>{{ labels.fareBasis }} : </span>
                            <strong>{{ element.pricing.fareBasis }}</strong>
                        </div>
                        <div *ngIf="element.pricing.fareFamily">
                            <span>{{ labels.fareFamily }} : </span>
                            <strong>{{ element.pricing.fareFamily }}</strong>
                        </div>
                        <div *ngIf="element.pricing.fareType && element.pricing.fareType.length">
                            <span>{{ labels.fareType }} : </span>
                            <strong *ngFor="let fareType of element.pricing.fareType">{{ fareTypeLabels[fareType] }}</strong>
                        </div>
                        <div *ngIf="element.pricing.tourcode">
                            <span>{{ labels.tourcode }} : </span>
                            <strong>{{ element.pricing.tourcode }}</strong>
                        </div>
                    </div>
                </td>
            </ng-container>

            <!-- Conditions Column -->
            <ng-container matColumnDef="conditions">
                <th mat-header-cell *matHeaderCellDef>
                    {{ labels.conditions }}
                </th>
                <td mat-cell *matCellDef="let element">
                    <cancellation-conditions
                            *ngIf="element.sector.type === bookingType.stay"
                            [keyword]="element.pricing?.keywords"
                            [dateLastCancellationWithoutPenalty]="element.whenLastToCancelWithoutPenalty"
                            [penalty]="element.condition.cancellation"
                            [price]="element.pricing.total"></cancellation-conditions>
                    <ng-container *ngIf="element.sector.type === bookingType.air">
                        <div *ngIf="element?.condition?.cancellation && element.condition.cancellation.length" class="cancellation">
                            <div class="title">{{ ruleTypeLabels.cancellation }} :</div>
                            <exchange *ngFor="let exchange of element.condition.cancellation;"
                                      [total]="element.pricing.total"
                                      [exchange]="exchange"></exchange>
                        </div>
                        <div *ngIf="element?.condition?.exchange && element.condition.exchange.length" class="exchange">
                            <div class="title">{{ ruleTypeLabels.exchange }} :</div>
                            <exchange *ngFor="let exchange of element.condition.exchange;"
                                      [total]="element.pricing.total"
                                      [exchange]="exchange"></exchange>
                        </div>
                        <div *ngIf="element?.condition?.luggage && element.condition.luggage.length">
                            <div class="title">{{ ruleTypeLabels.luggage }} :</div>
                            <luggage *ngFor="let condition of element.condition.luggage"
                                     [restriction]="condition.restriction"
                                     [type]="condition.in"></luggage>
                        </div>
                    </ng-container>

                </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="conditions.columns"></tr>
            <tr mat-row *matRowDef="let row; columns: conditions.columns;"></tr>
        </table>

        <div *ngIf="!conditions?.lines.length" class="noInformation">{{ labels.noCondition }}</div>
    `,
    styles: [`
        mat-table.mat-table, table[mat-table].mat-table {
            border: 0;
        }

        table tr.mat-header-row th.mat-header-cell,
        table .mat-row .mat-cell {
            padding: .75rem;
            border-color: rgba(189, 195, 199, 0.2);
            border-style: solid;
            border-width: 1px 1px 0 0;
            font-size: 1rem;
            text-align: left;
        }

        table .mat-row:nth-child(odd) .mat-cell {
            background-color: #f2f2f2;
        }

        table .mat-row .mat-cell {
            vertical-align: top;
            text-align: left;
        }

        table .mat-row .mat-cell&:first-child,
        table .mat-header-row .mat-header-cell&:first-child {
            width: 200px;
            max-width: inherit;
            background-color: #fff;
        }

        table .mat-row td.mat-cell.mat-column-sector.first-column {
            width: 225px;
            background-color: #fff;
        }

        table {
            margin: 0;
            border-collapse: collapse;
            width: 100%;
        }

        date-validity {
            display: block;
            margin-bottom: .75rem;
        }

        .keywords {
            margin-bottom: .75rem;
        }

        luggage:not(:last-child) {
            display: block;
            margin-bottom: .25rem;
        }

        .title, .exchange, .cancellation {
            margin-bottom: .75rem;
        }

        exchange {
            display: block;
        }

        .noInformation{
            padding: .75rem;
            color: #6E6E6E;
        }
    `]
})
export class ConditionsTabComponent {
    @Input()
    public conditions: ICondition;

    public bookingType: typeof BookingType = BookingType;

    public iconTags: IconDefinition = faTags;

    public labels: ILabels = {
        travellers: Yii.t('wui-shared-js', 'Voyageurs'),
        pricing: Yii.t('wui-shared-js', 'Tarification'),
        conditions: Yii.t('wui-shared-js', 'Conditions'),
        fareType: Yii.t('wui-shared-js', 'Type de tarif'),
        fareFamily: Yii.t('wui-shared-js', 'Fare family'),
        fareBasis: Yii.t('wui-shared-js', 'Farebasis'),
        company: Yii.t('wui-shared-js', 'Compagnie'),
        tourcode: Yii.t('wui-shared-js', 'Tour code'),
        with: Yii.t('wui-shared-js', 'avec'),
        noCondition: Yii.t('wui-shared-js', 'Aucune condition à afficher'),
        noCancellation: Yii.t('wui-shared-js', 'Aucune annulation possible'),
    };

    public ruleTypeLabels: { [key in RuleType]: string } = {
        [RuleType.cancellation]: Yii.t('wui-shared-js', 'Annulation'),
        [RuleType.exchange]: Yii.t('wui-shared-js', 'Echange'),
        [RuleType.luggage]: Yii.t('wui-shared-js', 'Bagages par voyageur'),
    };

    public keywordLabels: { [key in Keyword]: string } = {
        [Keyword.exchangeable]: Yii.t('wui-shared-js', 'Echangeable'),
        [Keyword.refundable]: Yii.t('wui-shared-js', 'Remboursable'),
        [Keyword.nonRefundable]: Yii.t('wui-shared-js', 'Non remboursable'),
    };

    public fareTypeLabels: { [key in ApiPricingRate]: string } = {
        negotiated: Yii.t('wui-shared-js', 'Négocié'),
        public: Yii.t('wui-shared-js', 'Public')
    };
}